import { useEffect, useState } from 'react';
import { auth } from '../firebase';
import { subscribeRealtime } from './realtime';
import { getUnreadMessageCount } from './secureFunctions';

/** Unread chat count + whether a call is ringing right now, kept live over the realtime stream. */
export function useInboxSignal(): { unread: number; ringing: boolean } {
  const [unread, setUnread] = useState(0);
  const [ringingCall, setRingingCall] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    let pending: number | null = null;
    const load = () => {
      if (pending !== null) return;
      pending = window.setTimeout(async () => {
        pending = null;
        try {
          const res = await getUnreadMessageCount();
          if (alive) setUnread(res.total || 0);
        } catch { /* signed out / offline */ }
      }, 300);
    };
    load();
    const unsub = subscribeRealtime((payload) => {
      if (!payload || typeof payload !== 'object') return;
      if (payload.type === 'chat_message' || payload.type === 'chat_read') {
        if (payload.type === 'chat_message' && payload.senderId === auth.currentUser?.uid) return;
        load();
      } else if (payload.type === 'call') {
        if (payload.kind === 'offer') setRingingCall(String(payload.callId || 'call'));
        else setRingingCall((cur) => (cur && cur === payload.callId ? null : cur));
        if (payload.kind === 'cancel' || payload.kind === 'end') load();
      }
    });
    const onFocus = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onFocus);
    const poll = window.setInterval(onFocus, 60_000);
    return () => {
      alive = false;
      unsub();
      if (pending !== null) window.clearTimeout(pending);
      document.removeEventListener('visibilitychange', onFocus);
      window.clearInterval(poll);
    };
  }, []);

  // Stop the ringing signal if no follow-up event arrives (e.g. caller's tab closed).
  useEffect(() => {
    if (!ringingCall) return undefined;
    const t = window.setTimeout(() => setRingingCall(null), 60_000);
    return () => window.clearTimeout(t);
  }, [ringingCall]);

  return { unread, ringing: !!ringingCall };
}
